import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MediationPriceHistory } from "@expertloop/shared";
import { CollapsibleCard } from "./CollapsibleCard";

function formatDate(value: Date | string): string {
	return new Date(value).toLocaleString();
}

/**
 * Évolution du prix convenu et mouvements d'argent réels, séparés : une remise
 * commerciale n'est pas un remboursement (MEDIATIONUX1).
 */
export function MediationPricePanel({ price, onOpenRef }: { price: MediationPriceHistory; onOpenRef: (ref: string) => void }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();
	const money = (cents: number) => formatCents(cents, price.currency);
	const { flows } = price;

	const flowRows: Array<{ label: string; value: number | undefined }> = [
		{ label: t("adminChatDetail.dossier.price.authorized"), value: flows.authorized },
		{ label: t("adminChatDetail.dossier.price.captured"), value: flows.captured },
		{ label: t("adminChatDetail.dossier.price.released"), value: flows.released },
		{ label: t("adminChatDetail.dossier.price.refunded"), value: flows.refunded },
		{ label: t("adminChatDetail.dossier.price.refundable"), value: flows.refundable },
		{ label: t("adminChatDetail.dossier.price.keptByExpert"), value: flows.keptByExpert },
	];

	return (
		<CollapsibleCard className="mediation-price" id="mediation-price" title={t("adminChatDetail.dossier.price.title")}>
			<div>
				<ol className="mediation-price-steps">
					{price.steps.map((step, index) => (
						<li key={`${step.kind}-${step.deliveryId ?? index}`} className="mediation-price-step">
							<div>
								<strong>{step.kind === "commercial_refund" ? `−${money(step.amount)}` : money(step.amount)}</strong>
								{step.kind !== "commercial_refund" && step.change !== undefined && step.change !== 0 && (
									<span className="mediation-price-change">
										{" "}
										{t("adminChatDetail.dossier.price.change", { amount: `${step.change > 0 ? "+" : "−"}${money(Math.abs(step.change))}` })}
									</span>
								)}
							</div>
							<div className="mediation-price-meta">
								{step.kind === "authorized"
									? t("adminChatDetail.dossier.price.stepAuthorized")
									: step.kind === "commercial_refund"
										? t("adminChatDetail.dossier.price.stepRefund")
										: t("adminChatDetail.dossier.price.stepDelivery")}
								{" · "}
								{formatDate(step.at)}
								{step.withdrawn && <span className="mediation-chip">{t("adminChatDetail.dossier.price.withdrawn")}</span>}
								{step.accepted && <span className="mediation-chip">{t("adminChatDetail.dossier.price.accepted")}</span>}
								{step.rejected && <span className="mediation-chip">{t("adminChatDetail.dossier.price.rejected")}</span>}
								{step.implicit && <span className="mediation-chip">{t("adminChatDetail.dossier.price.implicit")}</span>}
								{step.deliveryId && (
									<button type="button" className="btn btn-sm btn-outline mediation-open" onClick={() => onOpenRef(`delivery:${step.deliveryId}`)}>
										{t("adminChatDetail.dossier.timeline.open")}
									</button>
								)}
							</div>
						</li>
					))}
				</ol>

				{price.reduction && (
					<p className="mediation-price-reduction">
						<strong>{t("adminChatDetail.dossier.price.reductionTitle")}</strong>
						{": "}
						{price.reduction.percent} %{" · "}
						{t("adminChatDetail.dossier.price.reductionDetail", { amount: money(price.reduction.amount) })}
						{price.reduction.commercialRefund > 0 && (
							<span className="mediation-price-meta">
								{" "}
								(
								{t("adminChatDetail.dossier.price.reductionBreakdown", {
									discount: money(price.reduction.discount),
									refund: money(price.reduction.commercialRefund),
								})}
								)
							</span>
						)}
					</p>
				)}
				{price.netAmount !== price.agreedAmount && (
					<p className="mediation-price-reduction">
						<strong>{t("adminChatDetail.dossier.price.netPrice")}</strong>
						{": "}
						{money(price.netAmount)}
					</p>
				)}

				<h3>{t("adminChatDetail.dossier.price.flowsTitle")}</h3>
				<table className="mediation-flows">
					<tbody>
						{flowRows.map((row) => (
							<tr key={row.label}>
								<th scope="row">{row.label}</th>
								<td>{row.value === undefined ? t("adminChatDetail.dossier.price.notCaptured") : money(row.value)}</td>
							</tr>
						))}
					</tbody>
				</table>
				<p className="translation-indicative">{t("adminChatDetail.dossier.price.notRefund")}</p>
				<p className="translation-indicative">{t("adminChatDetail.dossier.price.refundNote")}</p>
			</div>
		</CollapsibleCard>
	);
}
